import { apiFetch } from '@/lib/api'
import { API_BASE } from '@/lib/config'
import type { AgentEvent, AgentFile, AgentPlanningTask } from '@/types'

interface RawAgentRunFile {
  name?: string
  path?: string
  type?: string
  size?: string
  size_bytes?: number
  content?: string
  media_type?: string
  mime_type?: string
  viewer?: string
  preview_kind?: string
  previewKind?: string
  is_readable?: boolean
  isReadable?: boolean
  download_url?: string
  downloadUrl?: string
  runId?: string
}

export interface AgentRunStateSnapshot {
  run_id: string
  status?: string
  missing?: boolean
  recovery_reason?: string
  runner_active?: boolean
  source?: string
  latest_text?: string
  files?: RawAgentRunFile[] | string[]
  computer_frames?: RawComputerFrame[]
  suggestions?: string[]
  events?: Array<Record<string, unknown>>
  steps?: Array<Record<string, unknown>>
  metrics?: Record<string, unknown>
  last_sequence_number?: number
}

export interface AgentRunEventsResponse {
  events: Array<Record<string, unknown>>
  last_sequence_number: number
}

export interface AgentRunResumeDecision {
  run_id: string
  status: string
  action: 'reconnect' | 'open_result' | 'retry' | 'retry_from_context' | string
  can_reconnect: boolean
  can_resume: boolean
  can_retry: boolean
  safe_checkpoint: boolean
  reason?: string
  last_sequence_number: number
  snapshot?: AgentRunStateSnapshot
}

export interface RawComputerFrame {
  id?: string
  kind?: string
  title?: string
  summary?: string
  timestamp?: string | number
  payload?: Record<string, unknown>
}

export interface HydratedBrowserFrame {
  id: string
  screenshot?: string
  url?: string
  title?: string
  message?: string
  action?: string
  step?: number
  status: 'starting' | 'running' | 'done' | 'error' | 'takeover'
  timestamp: number
}

export interface HydratedTerminalFrame {
  id: string
  command: string
  output?: string
  error?: string
  status: 'running' | 'done' | 'error'
  timestamp: number
}

function fileTypeFromPath(path: string, rawType?: string): AgentFile['type'] {
  if (rawType && ['markdown', 'code', 'archive', 'image', 'text', 'csv', 'json', 'html', 'pdf', 'document', 'spreadsheet', 'file', 'other'].includes(rawType)) {
    return rawType as AgentFile['type']
  }
  if (/\.mdx?$/i.test(path)) return 'markdown'
  if (/\.csv$/i.test(path)) return 'csv'
  if (/\.json$/i.test(path)) return 'json'
  if (/\.html?$/i.test(path)) return 'html'
  if (/\.pdf$/i.test(path)) return 'pdf'
  if (/\.docx?$/i.test(path)) return 'document'
  if (/\.xlsx?$/i.test(path)) return 'spreadsheet'
  if (/\.(ts|tsx|js|jsx|py|css|sh|sql)$/i.test(path)) return 'code'
  if (/\.(zip|tar|gz)$/i.test(path)) return 'archive'
  if (/\.(png|jpe?g|gif|webp|svg)$/i.test(path)) return 'image'
  if (/\.(txt|log)$/i.test(path)) return 'text'
  return 'other'
}

export function normalizeRunStateFiles(snapshot: AgentRunStateSnapshot): AgentFile[] {
  const runId = snapshot.run_id
  return (snapshot.files ?? [])
    .map((file): AgentFile | null => {
      if (typeof file === 'string') {
        return {
          name: file.split('/').pop() || file,
          path: file,
          type: fileTypeFromPath(file),
          runId,
          downloadUrl: `${API_BASE}/api/agent/${runId}/download/${file}`,
        }
      }
      const path = file.path || file.name || ''
      if (!path) return null
      return {
        name: file.name || path.split('/').pop() || path,
        path,
        size: file.size || (typeof file.size_bytes === 'number' ? `${Math.round(file.size_bytes / 1024)}KB` : undefined),
        type: fileTypeFromPath(path, file.type),
        content: file.content,
        runId,
        mediaType: file.media_type || file.mime_type,
        viewer: file.viewer,
        previewKind: file.previewKind || file.preview_kind,
        isReadable: file.isReadable ?? file.is_readable,
        downloadUrl: file.downloadUrl || file.download_url || `${API_BASE}/api/agent/${runId}/download/${path}`,
      }
    })
    .filter(Boolean) as AgentFile[]
}

export function normalizeRunStateEvents(snapshot: AgentRunStateSnapshot): AgentEvent[] {
  const seen = new Set<string>()
  return (snapshot.events ?? [])
    .map((event, index): AgentEvent | null => {
      const eventKey = String(event.id ?? event.sequence_number ?? `${event.type ?? 'event'}:${index}`)
      if (seen.has(eventKey)) return null
      seen.add(eventKey)
      const data = event.data && typeof event.data === 'object' && !Array.isArray(event.data)
        ? event.data as Record<string, unknown>
        : {}
      const type = String(event.type ?? data.type ?? 'message')
      const label = String(
        event.label
        ?? data.label
        ?? data.message
        ?? data.summary
        ?? data.status
        ?? type,
      ).trim()
      if (!label) return null
      const timestamp = typeof event.timestamp === 'number'
        ? event.timestamp
        : Date.parse(String(event.timestamp ?? '')) || Date.now()
      return {
        id: String(event.id ?? `${snapshot.run_id}:${event.sequence_number ?? index + 1}`),
        type: ['thinking', 'tool_call', 'tool_result', 'observation', 'plan_update', 'approval_needed', 'message'].includes(type)
          ? type as AgentEvent['type']
          : type === 'file_change'
            ? 'tool_result'
            : type === 'task_plan'
              ? 'plan_update'
              : 'message',
        label,
        tool: typeof data.tool === 'string' ? data.tool : typeof event.tool === 'string' ? event.tool : undefined,
        success: typeof data.success === 'boolean' ? data.success : typeof event.success === 'boolean' ? event.success : undefined,
        timestamp,
      }
    })
    .filter(Boolean) as AgentEvent[]
}

export function normalizeRunStatePlanningTasks(snapshot: AgentRunStateSnapshot): AgentPlanningTask[] {
  return (snapshot.steps ?? [])
    .map((step, index): AgentPlanningTask | null => {
      const label = String(step.label ?? step.text ?? step.title ?? '').trim()
      if (!label) return null
      const rawStatus = String(step.status ?? '').toLowerCase()
      const status: AgentPlanningTask['status'] =
        rawStatus === 'done' || rawStatus === 'completed' ? 'done'
          : rawStatus === 'error' || rawStatus === 'failed' ? 'error'
            : rawStatus === 'running' || rawStatus === 'active' ? 'active'
              : 'pending'
      return {
        id: typeof step.id === 'number' ? step.id : index + 1,
        label,
        status,
        tool_hint: typeof step.tool_hint === 'string' ? step.tool_hint : typeof step.tool === 'string' ? step.tool : undefined,
        chips: [],
        updates: [],
      }
    })
    .filter(Boolean) as AgentPlanningTask[]
}

function isAgentIntroPlaceholder(value: string): boolean {
  const text = value.trim().toLowerCase()
  return text === ''
    || text.startsWith('jeg har planen klar')
    || text.startsWith('jeg starter med')
    || text.startsWith('jeg leser oppgaven')
}

export function selectHydratedAgentContent(currentContent: string | undefined, snapshot: AgentRunStateSnapshot): string {
  const current = (currentContent ?? '').trim()
  const latest = (snapshot.latest_text ?? '').trim()
  if (!latest) return current
  const status = String(snapshot.status ?? '').toLowerCase()
  const terminal = ['completed', 'completed_with_warnings', 'failed', 'fatal_failed', 'stopped'].includes(status)
  if (isAgentIntroPlaceholder(current)) return latest
  if (terminal && latest.length > current.length + 40) return latest
  return current || latest
}

export async function fetchAgentRunState(runId: string): Promise<AgentRunStateSnapshot | null> {
  const snapshotRes = await apiFetch(`${API_BASE}/api/agent/${runId}/snapshot`)
  if (snapshotRes.ok) {
    const snapshot = await snapshotRes.json() as AgentRunStateSnapshot
    if (snapshot.missing || snapshot.status === 'missing' || snapshot.source === 'missing') return null
    return snapshot
  }
  if (snapshotRes.status === 404) return null

  const res = await apiFetch(`${API_BASE}/api/agent/${runId}/state`)
  if (!res.ok) return null
  const snapshot = await res.json() as AgentRunStateSnapshot
  if (snapshot.missing || snapshot.status === 'missing' || snapshot.source === 'missing') return null
  return snapshot
}

export async function fetchAgentRunEvents(runId: string, afterSeq = 0): Promise<AgentRunEventsResponse> {
  const res = await apiFetch(`${API_BASE}/api/agent/${runId}/events?after_seq=${Math.max(0, afterSeq)}`)
  if (!res.ok) return { events: [], last_sequence_number: afterSeq }
  const payload = await res.json() as Partial<AgentRunEventsResponse>
  return {
    events: Array.isArray(payload.events) ? payload.events : [],
    last_sequence_number: typeof payload.last_sequence_number === 'number' ? payload.last_sequence_number : afterSeq,
  }
}

export async function requestAgentRunResume(runId: string): Promise<AgentRunResumeDecision | null> {
  const res = await apiFetch(`${API_BASE}/api/agent/${runId}/resume`, { method: 'POST' })
  if (!res.ok) return null
  const payload = await res.json() as Partial<AgentRunResumeDecision>
  return {
    run_id: String(payload.run_id ?? runId),
    status: String(payload.status ?? 'resume_available'),
    action: String(payload.action ?? 'retry') as AgentRunResumeDecision['action'],
    can_reconnect: Boolean(payload.can_reconnect),
    can_resume: Boolean(payload.can_resume),
    can_retry: Boolean(payload.can_retry),
    safe_checkpoint: Boolean(payload.safe_checkpoint),
    reason: typeof payload.reason === 'string' ? payload.reason : undefined,
    last_sequence_number: typeof payload.last_sequence_number === 'number' ? payload.last_sequence_number : 0,
    snapshot: payload.snapshot,
  }
}

function toTimestamp(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Date.parse(String(value ?? ''))
  return Number.isFinite(parsed) ? parsed : Date.now()
}

export function normalizeRunStateComputerFrames(snapshot: AgentRunStateSnapshot): {
  browserFrame: HydratedBrowserFrame | null
  browserEvents: HydratedBrowserFrame[]
  terminalFrames: HydratedTerminalFrame[]
} {
  const browserEvents: HydratedBrowserFrame[] = []
  const terminalFrames: HydratedTerminalFrame[] = []

  for (const frame of snapshot.computer_frames ?? []) {
    const payload = frame.payload && typeof frame.payload === 'object' && !Array.isArray(frame.payload)
      ? frame.payload
      : {}
    const timestamp = toTimestamp(frame.timestamp)
    if (frame.kind === 'browser') {
      const status = String(payload.status ?? 'running')
      browserEvents.push({
        id: frame.id ?? `${snapshot.run_id}:browser:${browserEvents.length + 1}`,
        screenshot: typeof payload.screenshot === 'string' ? payload.screenshot : undefined,
        url: typeof payload.url === 'string' ? payload.url : undefined,
        title: typeof payload.title === 'string' ? payload.title : typeof frame.title === 'string' ? frame.title : undefined,
        message: typeof payload.message === 'string' ? payload.message : typeof frame.summary === 'string' ? frame.summary : undefined,
        action: typeof payload.action === 'string' ? payload.action : undefined,
        step: typeof payload.step === 'number' ? payload.step : undefined,
        status: status === 'starting' || status === 'done' || status === 'error' || status === 'takeover' ? status : 'running',
        timestamp,
      })
    }
    if (frame.kind === 'terminal') {
      const status = String(payload.status ?? 'running')
      terminalFrames.push({
        id: frame.id ?? `${snapshot.run_id}:terminal:${terminalFrames.length + 1}`,
        command: typeof payload.command === 'string' ? payload.command : '',
        output: typeof payload.output === 'string' ? payload.output : undefined,
        error: typeof payload.error === 'string' ? payload.error : undefined,
        status: status === 'done' || status === 'error' ? status : 'running',
        timestamp,
      })
    }
  }

  return {
    browserFrame: browserEvents.at(-1) ?? null,
    browserEvents,
    terminalFrames,
  }
}

export async function fetchActiveAgentRun(conversationId: string): Promise<AgentRunStateSnapshot | null> {
  const res = await apiFetch(`${API_BASE}/api/agent/active?conversation_id=${encodeURIComponent(conversationId)}`)
  if (!res.ok) return null
  const payload = await res.json() as { run?: AgentRunStateSnapshot | null }
  return payload.run ?? null
}
